/**
 * Detect embedding once at module load. Reading `window.top` can throw for a
 * cross-origin parent; that also means this window is embedded.
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export const embedded = isInIframe();

export type HostKind = 'top-level' | 'same-origin-frame' | 'cross-origin-frame';

/**
 * Same-origin frames share this app's storage partition, so a popup's
 * response reaches them. Every other (or unknown) ancestor chain, including
 * Foundry Preview and SharePoint pages, is treated as cross-origin: those
 * frames sign in silently or hand off to one top-level tab.
 */
export function hostKind(): HostKind {
  if (!embedded) return 'top-level';
  const ancestors = window.location.ancestorOrigins;
  if (!ancestors || ancestors.length === 0) return 'cross-origin-frame';
  return Array.from(ancestors).every((origin) => origin === window.location.origin)
    ? 'same-origin-frame'
    : 'cross-origin-frame';
}

export const host: HostKind = hostKind();

/**
 * MSAL's hidden iframes and popup callbacks navigate back to the application.
 * They must stay dormant so a nested SPA cannot consume the response or start
 * another auth flow. This is intentionally narrower than `embedded`: a normal
 * CDF preview iframe must still render the application.
 */
export function isMsalAuthTransportFrame(): boolean {
  const hasAuthResponseHash = /[#&?](code|state|error|error_description)=/.test(
    window.location.hash
  );
  const isSilentAuthIframe = embedded && hasAuthResponseHash;
  const isMsalPopup = Boolean(window.opener) && window.name.startsWith('msal');

  return isSilentAuthIframe || isMsalPopup;
}
